import { remove, filter, omit } from 'lodash';

import {
  ContentInput,
  Network,
  PublicUserProfile,
  S3Object,
  UrlMetadataImage,
  UserProfile,
} from 'api';
import { isAdminUser, isContributorUser } from 'helpers';

import { hasCommentary, hasDisclosures } from 'features/content/helpers';
import { bodyEndsWithUrl, getSafeBody, stripTrailingUrl } from 'features/posting/helpers';

const { REACT_APP_REGION } = process.env;

export const isOnlyModerator = (user: UserProfile | PublicUserProfile) => {
  const isModerator = user?.group_moderator?.length;
  return isModerator && !isAdminUser(user);
};

export const hasContributorPrivilege = (
  user: UserProfile | PublicUserProfile,
  moderatorMode: boolean,
) => {
  return isAdminUser(user) || isContributorUser(user) || (isOnlyModerator(user) && moderatorMode);
};

export const hasModeratorPrivilege = (
  user: UserProfile | PublicUserProfile,
  moderatorMode: boolean,
) => {
  return isAdminUser(user) || (isOnlyModerator(user) && moderatorMode);
};

export const formatBody = (content: ContentInput) => {
  const { content_object } = content;
  const { body, url } = content_object;

  if (bodyEndsWithUrl(url, body)) {
    return getSafeBody(stripTrailingUrl(url, body));
  }

  return getSafeBody(body);
};

export const formatCommentary = (content: ContentInput) => {
  if (!hasCommentary(content)) {
    return null;
  }

  return remove(content.commentary, commentary => !!commentary.commentary);
};

export const formatDisclosures = (content: ContentInput) => {
  if (!hasDisclosures(content)) {
    return [];
  }

  const disclosures = filter(content.disclosures, disclosure => !!disclosure.disclosure_id);
  return disclosures.map(item => omit(item, 'disclosure'));
};

export const getDisclosureNetwork = (network: Network) => {
  switch (network) {
    case 'facebook': {
      return Network.Facebook;
    }
    case 'twitter': {
      return Network.Twitter;
    }
    case 'linkedin': {
      return Network.Linkedin;
    }
    default: {
      return null;
    }
  }
};

export const convertUrlImageToPhoto = (image: UrlMetadataImage): S3Object => {
  return {
    bucket: image.bucket,
    key: image.file_key,
    width: image.width,
    height: image.height,
    region: REACT_APP_REGION,
  };
};
